// NX button (shadcn Button restyled): primary red fill, outline secondary, ghost, destructive and link; sizes default, sm, icon, icon-sm.
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "@/lib/cn";

const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-sm border text-btn font-medium whitespace-nowrap select-none",
    "transition-[background-color,border-color,box-shadow,color,transform] duration-150 ease-nx active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45",
    "aria-invalid:border-brand-text [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        default:
          "border-primary bg-primary font-semibold text-primary-foreground shadow-primary hover:border-primary-hover hover:bg-primary-hover",
        destructive:
          "border-primary bg-primary font-semibold text-primary-foreground shadow-primary hover:border-primary-hover hover:bg-primary-hover",
        outline: "border-border bg-card text-foreground hover:border-b3 hover:bg-accent",
        secondary: "border-border bg-card text-foreground hover:border-b3 hover:bg-accent",
        ghost: "border-transparent bg-transparent text-foreground hover:bg-accent",
        link: "border-transparent bg-transparent px-0 text-brand-blue underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5",
        sm: "h-[30px] gap-1.5 px-2.5 text-chip",
        lg: "h-[42px] px-4",
        icon: "size-8 p-0",
        "icon-sm": "size-7 p-0 [&_svg:not([class*='size-'])]:size-[15px]",
        "icon-lg": "size-9 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants, type ButtonProps };
